import React, { useState, useEffect } from "react";
import {
  Home,
  ChevronRight,
  Heart,
  MessageCircle,
  Share2,
  Calendar,
  User,
  Clock,
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";

const BlogDetails = () => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language || "en";
  
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [showFullContent, setShowFullContent] = useState(false);
  const [comments, setComments] = useState([]);

  // Helper function to get localized text
  const getLocalizedText = (item, field) => {
    if (!item || !item[field]) return "";
    
    // If value is a string (backward compatibility with old blogs)
    if (typeof item[field] === 'string') {
      return item[field];
    }
    
    // If value is an object with language keys (new multilingual format)
    if (typeof item[field] === 'object') {
      return item[field][currentLanguage] || 
             item[field].en || 
             item[field].hi || 
             item[field].es || 
             item[field].zh || 
             "";
    }
    
    return "";
  };

  useEffect(() => {
    fetchBlogDetails();
    fetchComments();
  }, [id]);

  // Update blog content when language changes
  useEffect(() => {
    if (blog) {
      const updatedBlog = {
        ...blog,
        localizedTitle: getLocalizedText(blog, "title"),
        localizedContent: getLocalizedText(blog, "content")
      };
      setBlog(updatedBlog);
    }
  }, [currentLanguage]);

  const fetchBlogDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/blog/get-blog-by-id/${id}`
      );
      
      const blogData = response.data;
      
      // If blog has multilingual structure, ensure we have the localized fields
      if (blogData.title && typeof blogData.title === 'object') {
        // It's a multilingual blog
        blogData.localizedTitle = getLocalizedText(blogData, "title");
        blogData.localizedContent = getLocalizedText(blogData, "content");
      } else {
        // It's an old blog with string fields
        blogData.localizedTitle = blogData.title || "";
        blogData.localizedContent = blogData.content || "";
      }
      
      setBlog(blogData);
      setLikesCount(blogData.likes || 0);
    } catch (error) {
      console.error("Error fetching blog details:", error);
      setError(t("failed_to_load_blog") || "Failed to load blog. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    // In a real app, you would fetch comments from API
    // For now, using static data
    setComments([
      {
        id: 1,
        name: "Annette Bank",
        text: t("comment_1_text"),
        date: "2024-12-13",
      },
      {
        id: 2,
        name: "Dominant Industries",
        text: t("comment_2_text"),
        date: "2024-12-14",
      },
    ]);
  };

  const toggleLike = () => {
    setLiked(!liked);
    setLikesCount(liked ? likesCount - 1 : likesCount + 1);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get blog content as array
  const getBlogContentArray = (blogContent) => {
    if (!blogContent) return [];
    
    if (Array.isArray(blogContent)) {
      return blogContent;
    }
    
    if (typeof blogContent === 'string') {
      // Split by newlines or periods to create paragraphs
      return blogContent.split(/\n+/).filter(p => p.trim() !== "");
    }
    
    return [blogContent];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10">
        <p className="text-red-500 mb-4">{error}</p>
        <button 
          onClick={() => navigate("/blogs")}
          className="bg-orange-500 text-white px-4 py-2 rounded-lg"
        >
          {t("back_to_blogs") || "Back to Blogs"}
        </button>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="text-center py-10">
        <p className="text-gray-500 mb-4">
          {t("blog_not_found") || "Blog not found"}
        </p>
        <button 
          onClick={() => navigate("/blogs")}
          className="bg-orange-500 text-white px-4 py-2 rounded-lg"
        >
          {t("back_to_blogs") || "Back to Blogs"}
        </button>
      </div>
    );
  }

  const blogTitle = blog.localizedTitle || getLocalizedText(blog, "title") || blog.title || t("untitled_blog");
  const blogContent = blog.localizedContent || getLocalizedText(blog, "content") || blog.content || "";
  const contentArray = getBlogContentArray(blogContent);

  return (
    <div className="bg-[#FBFBFB]">
      {/* Breadcrumb */}
      <header className="bg-gray-800 text-white py-4 px-4 md:px-10">
        <nav className="flex items-center space-x-2 text-sm">
          <button 
            onClick={() => navigate("/")}
            className="flex items-center hover:text-orange-400"
          >
            <Home className="w-4 h-4" />
          </button>
          <ChevronRight className="w-4 h-4" />
          <button 
            onClick={() => navigate("/blogs")}
            className="hover:text-orange-400"
          >
            {t("blogs") || "Blogs"}
          </button>
          <ChevronRight className="w-4 h-4" />
          <span className="text-orange-400 truncate max-w-xs md:max-w-md">
            {blogTitle}
          </span>
        </nav>
      </header>

      <main className="mx-auto px-4 md:px-10 py-10">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="lg:w-2/3">
            {/* Blog Image */}
            <div className="rounded-xl overflow-hidden mb-6">
              <img
                src={blog.image || "https://via.placeholder.com/800x400?text=Blog+Image"}
                alt={blogTitle}
                className="w-full h-auto max-h-[500px] object-cover"
                onError={(e) => {
                  e.target.src = "https://via.placeholder.com/800x400?text=Blog+Image";
                }}
              />
            </div>

            {/* Blog Actions */}
            <div className="flex items-center space-x-6 mb-6">
              <button onClick={toggleLike} className="flex items-center text-gray-600 hover:text-red-500">
                <Heart className={`w-5 h-5 mr-1 ${liked ? "text-red-500 fill-red-500" : ""}`} />
                <span>{likesCount}</span>
              </button>
              <button className="flex items-center text-gray-600 hover:text-blue-500">
                <MessageCircle className="w-5 h-5 mr-1" />
                <span>{blog.comments || comments.length}</span>
              </button>
              <button className="flex items-center text-gray-600 hover:text-gray-800">
                <Share2 className="w-5 h-5" />
                <span className="ml-1 text-sm">{t("share") || "Share"}</span>
              </button>
            </div>

            {/* Blog Title */}
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              {blogTitle}
            </h1>

            {/* Blog Meta */}
            <div className="flex flex-wrap items-center gap-4 text-gray-600 mb-6">
              <div className="flex items-center">
                <User className="w-4 h-4 mr-2" />
                <span>{blog.author || t("admin") || "Admin"}</span>
              </div>
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                <span>{formatDate(blog.createdAt)}</span>
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                <span>{blog.readTime || t("estimated_read_time") || "5 min read"}</span>
              </div>
            </div>

            {/* Blog Content */}
            <div className="prose max-w-none mb-8">
              {contentArray.map((paragraph, index) => (
                <p key={index} className="text-gray-700 mb-4 leading-relaxed">
                  {paragraph}
                </p>
              ))}

              {contentArray.length > 3 && (
                <button
                  onClick={() => setShowFullContent(!showFullContent)}
                  className="mt-4 bg-gray-900 hover:bg-gray-700 text-white px-4 py-2 rounded-md"
                >
                  {showFullContent 
                    ? t("show_less") || "Show Less" 
                    : t("read_full_article") || "Read Full Article"}
                </button>
              )}
            </div>

            {/* Comments Section */}
            {/* <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                {t("comments") || "Comments"} ({blog.comments || comments.length})
              </h3>

              <div className="space-y-4 mb-6">
                {comments.map((comment) => (
                  <div key={comment.id} className="border-b border-gray-200 pb-4">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold text-gray-900">{comment.name}</h4>
                      <span className="text-xs text-gray-400">
                        {formatDate(comment.date)}
                      </span>
                    </div>
                    <p className="text-gray-700">{comment.text}</p>
                  </div>
                ))}
              </div>
              <button className="text-orange-500 border hover:text-black border-orange-500 px-4 py-2 rounded-full">
                {t("load_more") || "Load More"}
              </button>
            </div> */}

            {/* Comment Form */}
            {/* <div className="bg-white p-6 rounded-lg shadow-sm">
              <h4 className="text-xl font-semibold text-gray-900 mb-4">
                {t("leave_comment") || "Leave a Comment"}
              </h4>

              <form className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">
                      {t("full_name") || "Full Name"}
                    </label>
                    <input
                      type="text"
                      className="w-full p-2 border border-gray-300 rounded-lg"
                      placeholder={t("your_name") || "Your name"}
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">
                      {t("email") || "Email"}
                    </label>
                    <input
                      type="email"
                      className="w-full p-2 border border-gray-300 rounded-lg"
                      placeholder={t("your_email") || "your@email.com"}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">
                    {t("your_comment") || "Your Comment"}
                  </label>
                    <textarea
                    className="w-full p-2 border border-gray-300 rounded-lg h-32"
                    placeholder={t("share_thoughts") || "Share your thoughts..."}
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg"
                >
                  {t("post_comment") || "Post Comment"}
                </button>
              </form>
            </div> */}
          </div>

          {/* Sidebar */}
          <div className="lg:w-1/3 space-y-6">
            {/* Popular Tags */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                {t("popular_tags") || "Popular Tags"}
              </h2>
              <div className="flex flex-wrap gap-2">
                {["Sculptures", "Handicrafts", "Home Decor", "Stone Art", "Metal Art", "Wooden Art"].map((tag, index) => (
                  <button
                    key={index}
                    onClick={() => navigate(`/blogs?tag=${tag.toLowerCase()}`)}
                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-orange-300 transition-colors"
                  >
                    {t(`tag_${tag.toLowerCase().replace(/\s+/g, '_')}`) || tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Gallery */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                {t("our_gallery") || "Our Gallery"}
              </h2>
              <div className="grid grid-cols-4 gap-2">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
                  <img
                    key={item}
                    src="https://devsabha.com/wp-content/uploads/2022/07/IMG_20220805_080447_resize_95.jpg"
                    alt={t("gallery_image") || "Gallery"}
                    className="w-full h-24 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => navigate("/gallery")}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BlogDetails;